import * as dust from '@density/dust/dist/tokens/dust.tokens';

import { FloorplanCoordinates, ImageCoordinates } from 'lib/geometry';
import Floorplan from 'lib/floorplan';
import { Viewport } from 'lib/viewport';
import { LengthUnit, displayLength } from 'lib/units';
import { degreesToRadians } from 'lib/math';
import FloorplanCollection from 'lib/floorplan-collection';
import PlanSensor from 'lib/sensor';
import Reference, { ReferenceRuler } from 'lib/reference';
import Measurement from 'lib/measurement';

const LEADER_LINE_STEM_LENGTH_PX = 32;
const END_POINT_RADIUS_PX = 4;

function getSensorExportColor(sensor: PlanSensor): string {
  if (!sensor.serialNumber) return dust.Gray400;
  if (sensor.type === 'oa')
    return sensor.sensorFunction === 'oaLongRange'
      ? dust.Teal400
      : dust.Blue400;
  if (sensor.type === 'waffle') return dust.Yellow400;
  return dust.Purple400;
}

/**
 * Draws a sensor marker with a rotation-indicating direction arrow.
 *
 * 0 degrees = north (12 o'clock). The canvas is rotated so the shape always
 * points in the sensor's installed direction.
 *
 * Shapes per type:
 *   OA     – filled square with upward direction notch
 *   Entry  – filled triangle (apex = direction of aim)
 *   Waffle – filled circle with dot at "north" edge
 */
function drawSensorMarker(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  sensor: PlanSensor
) {
  const color = getSensorExportColor(sensor);
  const half = size / 2;

  ctx.save();
  ctx.translate(x, y);

  // Rotation: sensor.rotation is degrees where 0 = north.
  // Canvas 0 rad = east (3 o'clock), so subtract 90° to align north with "up".
  ctx.rotate(degreesToRadians(sensor.rotation));

  ctx.globalAlpha = 0.7;
  ctx.fillStyle = color;
  ctx.strokeStyle = dust.White;
  ctx.lineWidth = 1.5;
  ctx.lineJoin = 'round';

  if (sensor.type === 'oa') {
    // Square body
    ctx.beginPath();
    ctx.rect(-half, -half, size, size);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Direction notch (small triangle cut-out pointing "up" / north)
    const notchH = size * 0.35;
    ctx.beginPath();
    ctx.moveTo(-half * 0.5, -half);
    ctx.lineTo(0, -half - notchH);
    ctx.lineTo(half * 0.5, -half);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
    ctx.strokeStyle = dust.White;
    ctx.stroke();
  } else if (sensor.type === 'entry') {
    // Triangle with apex pointing "up" (the aimed direction)
    const triW = size / 1.5;
    ctx.beginPath();
    ctx.moveTo(-triW, half);
    ctx.lineTo(0, -half - size * 0.15);
    ctx.lineTo(triW, half);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  } else {
    // Waffle – circle with a direction tick mark
    ctx.beginPath();
    ctx.arc(0, 0, half, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Small direction tick at the "north" edge
    ctx.beginPath();
    ctx.moveTo(0, -half * 0.4);
    ctx.lineTo(0, -half - size * 0.22);
    ctx.strokeStyle = dust.White;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  ctx.restore();
}

// A utility function to render a "tag" containing text centered on a point
function drawMetricLabel(
  ctx: CanvasRenderingContext2D,
  scaleRatio: number,
  center: ImageCoordinates,
  color: string,
  text: string
): number {
  const textHeight = 18;
  const textMargin = 2;

  ctx.font = `${textHeight / scaleRatio}px Helvetica`;
  const textWidth = ctx.measureText(text).width;

  const textBackgroundWidth = textWidth + textMargin * 2;
  const textBackgroundHeight = textHeight + textMargin * 2;

  ctx.beginPath();
  ctx.rect(
    center.x - textWidth / 2 - textMargin,
    center.y - textHeight / 2 - textMargin,
    textBackgroundWidth,
    textBackgroundHeight
  );
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();

  ctx.fillStyle = dust.White;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.fillText(text, center.x, center.y);

  return textBackgroundWidth;
}

// Given an arrow tip location and a point somewhere else along the line segment, render an
// arrowhead
function drawArrowHead(
  ctx: CanvasRenderingContext2D,
  scaleRatio: number,
  color: string,
  arrowTip: ImageCoordinates,
  lineTail: ImageCoordinates
) {
  const angle = Math.atan2(lineTail.y - arrowTip.y, lineTail.x - arrowTip.x);

  const arrowSideLength = 20;
  const arrowDegrees = 20;

  const pointA = ImageCoordinates.create(
    arrowTip.x +
      arrowSideLength * Math.cos(angle + degreesToRadians(arrowDegrees)),
    arrowTip.y +
      arrowSideLength * Math.sin(angle + degreesToRadians(arrowDegrees))
  );

  const pointB = ImageCoordinates.create(
    arrowTip.x +
      arrowSideLength * Math.cos(angle - degreesToRadians(arrowDegrees)),
    arrowTip.y +
      arrowSideLength * Math.sin(angle - degreesToRadians(arrowDegrees))
  );

  ctx.beginPath();
  ctx.moveTo(arrowTip.x, arrowTip.y);
  ctx.lineTo(pointA.x, pointA.y);
  ctx.lineTo(pointB.x, pointB.y);
  ctx.closePath();
  ctx.fillStyle = dust.Yellow400;
  ctx.fill();
}

// A utility function to draw an dimension line on a canvas
function drawDimension(
  ctx: CanvasRenderingContext2D,
  scaleRatio: number,
  sensorCountBarHeight: number,
  floorplan: Floorplan,
  viewport: Viewport,
  displayUnit: LengthUnit,
  color: string,
  positionA: FloorplanCoordinates,
  positionB: FloorplanCoordinates,
  distanceLabelPosition?: FloorplanCoordinates
) {
  let positionAImageCoordinates = FloorplanCoordinates.toImageCoordinates(
    positionA,
    floorplan
  );
  let positionBImageCoordinates = FloorplanCoordinates.toImageCoordinates(
    positionB,
    floorplan
  );

  let distanceLabelPositionImageCoordinates = distanceLabelPosition
    ? FloorplanCoordinates.toImageCoordinates(distanceLabelPosition, floorplan)
    : null;

  // shift everything down by the top bar height
  positionAImageCoordinates.y =
    positionAImageCoordinates.y + sensorCountBarHeight / scaleRatio;
  positionBImageCoordinates.y =
    positionBImageCoordinates.y + sensorCountBarHeight / scaleRatio;
  if (distanceLabelPositionImageCoordinates) {
    distanceLabelPositionImageCoordinates.y =
      distanceLabelPositionImageCoordinates.y +
      sensorCountBarHeight / scaleRatio;
  }

  // Render reference line
  ctx.beginPath();
  ctx.moveTo(positionAImageCoordinates.x, positionAImageCoordinates.y);
  ctx.lineTo(positionBImageCoordinates.x, positionBImageCoordinates.y);
  ctx.closePath();

  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  ctx.stroke();

  // Make endpoints
  ctx.beginPath();
  ctx.ellipse(
    positionAImageCoordinates.x,
    positionAImageCoordinates.y,
    END_POINT_RADIUS_PX,
    END_POINT_RADIUS_PX,
    0,
    0,
    degreesToRadians(360)
  );
  ctx.ellipse(
    positionBImageCoordinates.x,
    positionBImageCoordinates.y,
    END_POINT_RADIUS_PX,
    END_POINT_RADIUS_PX,
    0,
    0,
    degreesToRadians(360)
  );
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();

  const floorplanX = positionB.x - positionA.x;
  const floorplanY = positionB.y - positionA.y;
  const distanceMeters = Math.hypot(floorplanX, floorplanY);
  const distanceText = displayLength(distanceMeters, displayUnit);

  // Draw metric label
  let metricLabelPosition = distanceLabelPositionImageCoordinates;
  if (!metricLabelPosition) {
    metricLabelPosition = FloorplanCoordinates.toImageCoordinates(
      ReferenceRuler.calculateCenterPoint({ positionA, positionB }),
      floorplan
    );
  }
  const distanceTextBackgroundWidth = drawMetricLabel(
    ctx,
    scaleRatio,
    metricLabelPosition,
    color,
    distanceText
  );

  // Draw leader line if:
  // 1. There's an explicit distance label position
  // 2. That distance label position isn't in the center of the reference line
  if (!distanceLabelPosition || !distanceLabelPositionImageCoordinates) {
    return;
  }

  const isDistanceLockedToCenter = ReferenceRuler.isDistanceLockedToCenter(
    floorplan,
    viewport,
    distanceLabelPosition,
    ReferenceRuler.calculateCenterPoint({ positionA, positionB })
  );

  if (isDistanceLockedToCenter) {
    return;
  }

  ctx.lineWidth = 1;

  ctx.beginPath();
  const centerPointX =
    (positionAImageCoordinates.x + positionBImageCoordinates.x) / 2;
  const centerPointY =
    (positionAImageCoordinates.y + positionBImageCoordinates.y) / 2;

  let leaderLineStartX: number;
  if (centerPointX > distanceLabelPositionImageCoordinates.x) {
    // Draw the leader line to the right of the metric label
    leaderLineStartX =
      distanceLabelPositionImageCoordinates.x + distanceTextBackgroundWidth / 2;
    ctx.moveTo(leaderLineStartX, distanceLabelPositionImageCoordinates.y);
    ctx.lineTo(
      leaderLineStartX + LEADER_LINE_STEM_LENGTH_PX,
      distanceLabelPositionImageCoordinates.y
    );
  } else {
    // Draw the leader line to the left of the metric label
    leaderLineStartX =
      distanceLabelPositionImageCoordinates.x - distanceTextBackgroundWidth / 2;
    ctx.moveTo(leaderLineStartX, distanceLabelPositionImageCoordinates.y);
    ctx.lineTo(
      leaderLineStartX - LEADER_LINE_STEM_LENGTH_PX,
      distanceLabelPositionImageCoordinates.y
    );
  }
  ctx.lineTo(centerPointX, centerPointY);
  ctx.strokeStyle = color;
  ctx.stroke();

  // Make midpoint
  drawArrowHead(
    ctx,
    scaleRatio,
    dust.Yellow400,
    ImageCoordinates.create(centerPointX, centerPointY),
    ImageCoordinates.create(
      leaderLineStartX,
      distanceLabelPositionImageCoordinates.y
    )
  );
}

export async function generateExportCanvas(
  floorplanImage: HTMLImageElement,
  sensors: FloorplanCollection<PlanSensor>,
  references: FloorplanCollection<Reference>,
  floorplan: Floorplan,
  viewport: Viewport,
  measurement: Measurement | undefined,
  displayUnit: LengthUnit
): Promise<HTMLCanvasElement> {
  // ====================================
  // Sensor Count bar (fixed 30px height)
  // ------------------------------------
  //
  // Floorplan (scaled to 2000px width)
  //
  // ====================================
  const canvas = document.createElement('canvas');
  const sensorCountBarHeight = 30;
  const desiredWidth = 5000;
  const scaleRatio = desiredWidth / floorplanImage.width;
  canvas.width = floorplanImage.width * scaleRatio;
  canvas.height = floorplanImage.height * scaleRatio + sensorCountBarHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas 2D context');

  ctx.scale(scaleRatio, scaleRatio);

  // white background (behind lowered opacity floorplan)
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(
    0,
    sensorCountBarHeight / scaleRatio,
    floorplanImage.width,
    floorplanImage.height
  );

  // draw floorplan image @ lowered opacity
  // This should be easy... but because of CORS related reasons, it is not
  // So a) hack around a CORS cache issue in chrome, and b) get this image with fetch because I
  // can't figure out how to configure CORS to work right with a `new Image()`
  ctx.globalAlpha = 0.6;
  let imageSrc = floorplanImage.src;
  if (imageSrc.includes('s3.amazonaws.com')) {
    // In order to ensure chrome doesn't cache the base image, a cache-busting query parameter is
    // needed. See https://github.com/DensityCo/nashi-client/blob/master/src/utils/downloadImage.ts
    // for where I got this technique, or talk to wei-wei for more info.
    const result = new URL(imageSrc);
    result.searchParams.set('nonce', `${new Date().getTime()}`);
    imageSrc = result.toString();
  }
  const response = await fetch(imageSrc);
  if (!response.ok) {
    throw new Error('Failed to fetch floorplan image!');
  }
  const imageBlob = await response.blob();
  const imageURL = URL.createObjectURL(imageBlob);

  // Wrap bytes into a new Image element
  const newImage = new Image();
  newImage.src = imageURL;
  await new Promise((resolve) => newImage.addEventListener('load', resolve));

  ctx.drawImage(newImage, 0, sensorCountBarHeight / scaleRatio);
  ctx.globalAlpha = 1.0;

  // sensor count bar
  ctx.fillStyle = dust.Gray800;
  ctx.fillRect(0, 0, floorplanImage.width, sensorCountBarHeight / scaleRatio);

  const countOA = PlanSensor.filterByType('oa', sensors).length;
  const countEntry = PlanSensor.filterByType('entry', sensors).length;
  const countWaffle = PlanSensor.filterByType('waffle', sensors).length;

  ctx.font = `${16 / scaleRatio}px Helvetica`;
  ctx.fillStyle = dust.White;

  const sensorLegendWidth = 15 / scaleRatio;
  const legendY = sensorCountBarHeight / scaleRatio / 2;

  // --- OA legend (square) ---
  const oaLegendX = 30 / scaleRatio;
  ctx.fillStyle = dust.Blue400;
  ctx.beginPath();
  ctx.rect(
    oaLegendX - sensorLegendWidth / 2,
    legendY - sensorLegendWidth / 2,
    sensorLegendWidth,
    sensorLegendWidth
  );
  ctx.closePath();
  ctx.fill();
  ctx.lineWidth = 2 / scaleRatio;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = dust.White;
  ctx.stroke();
  ctx.fillStyle = dust.White;
  ctx.fillText(`OA: ${countOA}`, 50 / scaleRatio, 21 / scaleRatio);

  // --- Entry legend (triangle) ---
  const entryLegendX = 140 / scaleRatio;
  ctx.fillStyle = dust.Purple400;
  ctx.beginPath();
  ctx.moveTo(
    entryLegendX - sensorLegendWidth / 1.8,
    legendY + sensorLegendWidth / 2
  );
  ctx.lineTo(entryLegendX, legendY - sensorLegendWidth / 2);
  ctx.lineTo(
    entryLegendX + sensorLegendWidth / 1.8,
    legendY + sensorLegendWidth / 2
  );
  ctx.closePath();
  ctx.fill();
  ctx.lineWidth = 2 / scaleRatio;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = dust.White;
  ctx.stroke();
  ctx.fillStyle = dust.White;
  ctx.fillText(`Entry: ${countEntry}`, 158 / scaleRatio, 21 / scaleRatio);

  // --- Waffle legend (circle) ---
  const waffleLegendX = 280 / scaleRatio;
  ctx.fillStyle = dust.Yellow400;
  ctx.beginPath();
  ctx.arc(waffleLegendX, legendY, sensorLegendWidth / 2, 0, Math.PI * 2);
  ctx.closePath();
  ctx.fill();
  ctx.lineWidth = 2 / scaleRatio;
  ctx.strokeStyle = dust.White;
  ctx.stroke();
  ctx.fillStyle = dust.White;
  ctx.fillText(`Waffle: ${countWaffle}`, 298 / scaleRatio, 21 / scaleRatio);

  // sensor markers (with per-sensor rotation and type-specific shapes)
  const sensorMarkerWidth = 18;
  Array.from(sensors.items).forEach(([, s]) => {
    let { x, y } = FloorplanCoordinates.toImageCoordinates(
      s.position,
      floorplan
    );
    y = y + sensorCountBarHeight / scaleRatio;

    ctx.shadowColor = dust.Gray800;
    ctx.shadowBlur = 10;
    drawSensorMarker(ctx, x, y, sensorMarkerWidth, s);
  });

  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;

  // Reference lines
  Array.from(references.items).forEach(([, reference]) => {
    if (reference.type !== 'ruler') {
      return;
    }
    if (!reference.enabled) {
      return;
    }

    drawDimension(
      ctx,
      scaleRatio,
      sensorCountBarHeight,
      floorplan,
      viewport,
      displayUnit,
      dust.Yellow400,
      reference.positionA,
      reference.positionB,
      reference.distanceLabelPosition
    );
  });

  // sensor marker serial number labels
  Array.from(sensors.items).forEach(([, s]) => {
    if (!s.serialNumber) return;

    let { x, y } = FloorplanCoordinates.toImageCoordinates(
      s.position,
      floorplan
    );
    y = y + sensorCountBarHeight / scaleRatio;

    const color = getSensorExportColor(s);

    const labelOffset =
      s.type === 'oa'
        ? sensorMarkerWidth / 2 + sensorMarkerWidth * 0.5 + 12
        : sensorMarkerWidth / 2 + sensorMarkerWidth * 0.5 + 8;

    drawMetricLabel(
      ctx,
      scaleRatio,
      ImageCoordinates.create(x, y - labelOffset),
      color,
      s.serialNumber
    );
  });

  // Floorplan scale
  if (measurement) {
    drawDimension(
      ctx,
      scaleRatio,
      sensorCountBarHeight,
      floorplan,
      viewport,
      displayUnit,
      dust.Blue400,
      ImageCoordinates.toFloorplanCoordinates(measurement.pointA, floorplan),
      ImageCoordinates.toFloorplanCoordinates(measurement.pointB, floorplan)
    );
  }

  return canvas;
}

export function storeCanvasToDisk(
  canvas: HTMLCanvasElement,
  fileName: string = 'export',
  imageType: 'png' | 'jpg' = 'png'
) {
  const link = document.createElement('a');
  const outputFile = `${fileName}.${imageType}`;
  const uri = canvas.toDataURL(`image/${imageType}`, 1.0);

  if (typeof link.download === 'string') {
    link.href = uri;
    link.download = outputFile;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    window.open(uri);
  }
}
